interface AssessmentValueProps {
  onStart?: (() => void) | undefined;
}

interface AssessmentCheck {
  number: string;
  icon: string;
  label: string;
  compactLabel: string;
  title: string;
  description: string;
  output: string;
  shortOutput: string;
  href: string;
}

export const assessmentChecks: readonly AssessmentCheck[] = [
  {
    number: "01",
    icon: "◷",
    label: "Cash runway",
    compactLabel: "Cash runway",
    title: "How long could the household keep paying the bills?",
    description: "Accessible money ÷ essential expenses and required debt payments, shown in days and months.",
    output: "Runway and first buffer gap",
    shortOutput: "Days covered + buffer gap",
    href: "/guides/how-long-will-my-emergency-fund-last/"
  },
  {
    number: "02",
    icon: "+",
    label: "Healthcare costs",
    compactLabel: "Health-cost gap",
    title: "Who is covered, and how much might still fall on savings?",
    description: "Test public, employer and private protection for every household member against a cost you choose.",
    output: "Per-person exposure and missing details",
    shortOutput: "Cost − usable cover",
    href: "/guides/is-my-health-insurance-enough/"
  },
  {
    number: "03",
    icon: "↳",
    label: "Income and life protection",
    compactLabel: "Income protection",
    title: "What would the people relying on each income actually receive?",
    description: "Compare debts and family-support needs with survivor income, usable assets and reported benefits.",
    output: "Family-support gap by earner",
    shortOutput: "Support need − resources",
    href: "/guides/is-my-life-insurance-enough/"
  },
  {
    number: "04",
    icon: "⌘",
    label: "Household dependency map",
    compactLabel: "Dependency map",
    title: "Who relies on whom?",
    description: "Connect each income to a partner, child, parent or other dependant instead of using a household-size score.",
    output: "People and exposed connections",
    shortOutput: "People ↔ incomes ↔ cover",
    href: "/sample-household/"
  },
  {
    number: "05",
    icon: "↓",
    label: "Debt pressure",
    compactLabel: "Debt pressure",
    title: "Are required payments already squeezing the household?",
    description: "Check overdue debt, monthly payment pressure and the conflict between expensive debt and other plans.",
    output: "Immediate debt warning and next action",
    shortOutput: "Overdue + payment pressure",
    href: "/guides/what-to-do-before-investing/"
  },
  {
    number: "06",
    icon: "◎",
    label: "Goal funding",
    compactLabel: "Goal funding",
    title: "What does the first goal require each month?",
    description: "Use the target, amount already saved and time remaining without hiding an assumed investment return.",
    output: "Goal gap and monthly checkpoint",
    shortOutput: "Gap + monthly checkpoint",
    href: "/guides/complete-personal-finance-checklist/"
  },
  {
    number: "07",
    icon: "◇",
    label: "Retirement and benefits",
    compactLabel: "Retirement & benefits",
    title: "Which long-term answers still need a local check?",
    description: "Record pensions, employer arrangements, public benefits and tax-advantaged accounts without treating unknowns as safe.",
    output: "Reviewed facts and local unknowns",
    shortOutput: "Benefits + local unknowns",
    href: "/methodology/"
  },
  {
    number: "08",
    icon: "1",
    label: "One risk order",
    compactLabel: "Priority order",
    title: "Which problem should be handled first?",
    description: "The deterministic engine compares every result and surfaces the most urgent, actionable household exposure.",
    output: "One priority, the evidence and one action",
    shortOutput: "First risk + next action",
    href: "/sample-household/"
  }
];

export function CompactAssessmentLinks({ className = "" }: { className?: string }) {
  return (
    <nav className={`compact-assessment-links ${className}`.trim()} aria-label="Eight calculations included in the assessment">
      {assessmentChecks.map((check) => (
        <a key={check.number} href={check.href}>
          <span className="compact-check-icon" aria-hidden="true">{check.icon}</span>
          <span><strong>{check.compactLabel}</strong><small>{check.shortOutput}</small></span>
          <b aria-hidden="true">→</b>
        </a>
      ))}
    </nav>
  );
}

export function AssessmentValue({ onStart }: AssessmentValueProps) {
  return (
    <section className="assessment-value" id="included-checks" aria-labelledby="included-checks-heading">
      <div className="page-width">
        <div className="assessment-value-heading">
          <div>
            <p className="eyebrow">Eight connected checks · one saved assessment</p>
            <h2 id="included-checks-heading">The calculators aren’t separate. They’re already inside MoneyMap.</h2>
          </div>
          <p>Enter each fact once. Cash, healthcare, family protection, debt and goals are then tested together, so one reassuring answer cannot hide a more urgent problem.</p>
        </div>
        <div className="assessment-check-grid">
          {assessmentChecks.map((check) => (
            <a className="assessment-check-card" key={check.number} href={check.href}>
              <div className="assessment-check-top"><span>{check.number}</span><small>{check.label}</small></div>
              <h3>{check.title}</h3>
              <p>{check.description}</p>
              <strong><span>YOU GET</span>{check.output}</strong>
              <span className="assessment-check-action">See the calculation →</span>
            </a>
          ))}
        </div>
        <div className="assessment-value-cta">
          <div><strong>Free shows your three highest-priority findings.</strong><span>No card. No product sales. Every calculation and unknown is visible.</span></div>
          {onStart
            ? <button className="primary-button large" type="button" onClick={onStart}>Run all eight checks →</button>
            : <a className="primary-button large" href="#planning-country">Choose my country and start →</a>}
        </div>
      </div>
    </section>
  );
}
